import { useEffect, useState } from 'react';

// -- Feminist color palette (local copy — same values as journey.tsx / mural.tsx,
//    plus the gold used for "waiting" and the teacher's note) --
export const P = {
  maxRed: '#D41920',
  sizzle: '#E84B5A',
  charm: '#F2A0C4',
  baby: '#F8D7DD',
  black: '#1A1018',
  white: '#FFFFFF',
  cream: '#FFF8F5',
  offWhite: '#FEF3EF',
  blush: '#FDF0ED',
  roseMist: '#F9E4E0',
  warmGray: '#756560',
  darkText: '#2D1A1F',
  midText: '#6B4F55',
  lightText: '#8A6F69',
  lightBorder: '#F0DAD5',
  green: '#1E7A46',
  greenSoft: '#E4F2E9',
  gold: '#B8862B',
  goldSoft: '#FBF1DC',
  shadow: 'rgba(180, 80, 80, 0.12)',
  shadowDeep: 'rgba(120, 40, 40, 0.18)',
};

export const oswald = "'Oswald', sans-serif";
export const dmSerif = "'DM Serif Text', serif";

// -- Responsive hook (matches journey.tsx / mural.tsx convention) --
export const useWidth = () => {
  const [w, setW] = useState(
    typeof window !== 'undefined' ? window.innerWidth : 640,
  );
  useEffect(() => {
    const h = () => setW(window.innerWidth);
    window.addEventListener('resize', h);
    return () => window.removeEventListener('resize', h);
  }, []);
  return w;
};

// Keyboard/click activation props for interactive cards (same as mural.tsx).
export function pressable(onActivate: () => void) {
  return {
    role: 'button' as const,
    tabIndex: 0,
    onClick: onActivate,
    onKeyDown: (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onActivate();
      }
    },
  };
}

// -- Type styles lifted from the prototype's stylesheet --
export const eyebrowStyle: React.CSSProperties = {
  fontFamily: oswald,
  fontWeight: 600,
  fontSize: 12,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: P.maxRed,
};

export const h1Style: React.CSSProperties = {
  fontFamily: oswald,
  fontWeight: 700,
  fontSize: 40,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  margin: '4px 0 6px',
  lineHeight: 1,
  color: P.darkText,
};

export const h2Style: React.CSSProperties = {
  fontFamily: dmSerif,
  fontWeight: 400,
  fontSize: 30,
  lineHeight: 1.1,
  margin: '0 0 10px',
  color: P.darkText,
  textWrap: 'balance',
} as React.CSSProperties;

export const h3Style: React.CSSProperties = {
  fontFamily: oswald,
  fontWeight: 600,
  fontSize: 13,
  letterSpacing: '0.18em',
  textTransform: 'uppercase',
  margin: '26px 0 10px',
  color: P.midText,
};

export const subStyle: React.CSSProperties = {
  color: P.midText,
  maxWidth: '60ch',
  margin: '0 0 10px',
  fontSize: 15,
  lineHeight: 1.55,
};

export const fineStyle: React.CSSProperties = {
  fontSize: 12,
  color: P.lightText,
  lineHeight: 1.5,
};

// Big square-cornered Oswald buttons (the prototype's `.btn`). Applied as
// `style` on top of @org/ui's <Button> so focus/disabled behaviour stays shared.
const btnBase: React.CSSProperties = {
  fontFamily: oswald,
  fontWeight: 600,
  fontSize: 14,
  letterSpacing: '0.12em',
  textTransform: 'uppercase',
  height: 'auto',
  padding: '12px 20px',
  borderRadius: 4,
  border: `2px solid ${P.black}`,
  lineHeight: 1.2,
};
export const btnRed: React.CSSProperties = {
  ...btnBase,
  background: P.maxRed,
  borderColor: P.maxRed,
  color: P.white,
};
export const btnBlack: React.CSSProperties = {
  ...btnBase,
  background: P.black,
  borderColor: P.black,
  color: P.white,
};
export const btnGhost: React.CSSProperties = {
  ...btnBase,
  background: 'transparent',
  borderColor: P.black,
  color: P.black,
};
export const btnSmall: React.CSSProperties = {
  padding: '8px 14px',
  fontSize: 12,
};

// -- Injected styles (hover/focus affordances, honoring reduced motion) --
export const PRODUCTS_STYLES = `
.products-poster { transition: transform .15s ease; }
.products-poster.is-pressable:hover { transform: translateY(-3px) rotate(-.6deg); }
.products-poster:focus-visible { outline: 3px solid ${P.charm}; outline-offset: 2px; }
.products-poster.is-pending { outline: 3px dashed ${P.gold}; outline-offset: 3px; }
.products-poster.is-pending:focus-visible { outline-color: ${P.charm}; }
.products-poster.is-expired { filter: grayscale(1) opacity(.75); }
.products-btn:hover { filter: brightness(1.06); }
.products-link:hover { text-decoration: underline; }
.products-link:focus-visible { outline: 3px solid ${P.charm}; outline-offset: 2px; }
.products-spread { display: grid; grid-template-columns: minmax(220px, 300px) 1fr; gap: 28px; align-items: start; }
.products-cover { position: sticky; top: 60px; }
@media (max-width: 640px) {
  .products-spread { grid-template-columns: 1fr; }
  .products-cover { position: static; }
  .products-cover .products-poster { max-width: 260px; margin: 0 auto; }
}
.products-carousel::-webkit-scrollbar { display: none; }
.products-carousel:focus-visible { outline: 3px solid ${P.charm}; outline-offset: 2px; border-radius: 3px; }
.products-sheet { max-width: 520px; margin: 0 auto; }
@media (prefers-reduced-motion: reduce) {
  .products-poster { transition: none; }
  .products-poster.is-pressable:hover { transform: none; }
}
`;
